import React, { useState } from 'react';
import { List, Typography, Tag, Progress, Tooltip, Space, Badge, Button, Collapse, Divider } from 'antd';
import {
    SyncOutlined,
    SendOutlined,
    PrinterOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    LoadingOutlined,
    ExclamationCircleOutlined,
    ShoppingOutlined,
    InfoCircleOutlined,
    EyeOutlined,
    FileOutlined
} from '@ant-design/icons';
import { getTaskDisplayInfo, formatTimeAgo, calculateProgress } from './utils/taskUtils';

const { Text } = Typography;

const TaskItem = ({ task, index, onRefresh }) => {
    const [showDetails, setShowDetails] = useState(false);
    const displayInfo = getTaskDisplayInfo(task);
    const progress = calculateProgress(task);

    // Get status color and icon
    const getStatusDisplay = (status) => {
        const statusMap = {
            waiting: { color: '#faad14', icon: <ClockCircleOutlined /> },
            active: { color: '#1890ff', icon: <LoadingOutlined spin /> },
            completed: { color: '#52c41a', icon: <CheckCircleOutlined /> },
            failed: { color: '#f5222d', icon: <ExclamationCircleOutlined /> },
            delayed: { color: '#722ed1', icon: <ClockCircleOutlined /> }
        };
        return statusMap[status] || { color: '#d9d9d9', icon: <ClockCircleOutlined /> };
    };

    const statusDisplay = getStatusDisplay(task.status);

    return (
        <div style={{ width: '100%' }}>
            {/* Header: Task name, icon và status */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 8
            }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flex: 1 }}>
                    {displayInfo.icon}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Text strong style={{
                            fontSize: 14,
                            color: '#262626',
                            display: 'block',
                            lineHeight: 1.4
                        }}>
                            {displayInfo.title}
                        </Text>
                        {task.data?.taskName && (
                            <Text style={{
                                fontSize: 12,
                                color: '#666',
                                fontStyle: 'italic',
                                display: 'block',
                                marginTop: 2,
                                lineHeight: 1.3
                            }}>
                                {task.data.taskName}
                            </Text>
                        )}
                    </div>
                </div>

                {/* Status tag - góc phải trên */}
                <Tag
                    color={statusDisplay.color}
                    icon={statusDisplay.icon}
                    style={{
                        margin: 0,
                        fontSize: 9,
                        borderRadius: 8,
                        paddingLeft: 6,
                        paddingRight: 6,
                        fontWeight: 600,
                        height: 20,
                        lineHeight: '20px',
                        border: 'none'
                    }}
                >
                    {task.status.toUpperCase()}
                </Tag>
            </div>

            {/* User info và timing - compact layout */}
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: 12,
                padding: '6px 8px',
                backgroundColor: '#fafafa',
                borderRadius: 6,
                border: '1px solid #f0f0f0'
            }}>
                {/* User info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {task.data?.userEmail && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4
                        }}>
                            <div style={{
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                backgroundColor: '#1890ff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 9,
                                color: '#fff',
                                fontWeight: 600
                            }}>
                                {task.data.userEmail.split('@')[0][0].toUpperCase()}
                            </div>
                            <Text style={{
                                fontSize: 10,
                                color: '#666',
                                fontWeight: 500
                            }}>
                                {task.data.userEmail.split('@')[0]}
                            </Text>
                        </div>
                    )}
                    <Divider type='vertical' style={{ height: 16, margin: '0' }} />
                    {/* Details button */}
                    <Tooltip title="View task details">
                        <Button
                            type="text"
                            icon={<FileOutlined />}
                            onClick={() => setShowDetails(!showDetails)}
                            style={{
                                fontSize: 12,
                                height: 18,
                                width: 20,
                                padding: 0,
                                color: showDetails ? '#1890ff' : '#999',
                                backgroundColor: showDetails ? '#e6f7ff' : 'transparent'
                            }}
                        />
                    </Tooltip>
                </div>
                
                {/* Timing info - compact */}
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 10,
                    color: '#999'
                }}>
                    <Tooltip title={`Created: ${new Date(task.timestamp).toLocaleString()}`}>
                        <span><ClockCircleOutlined/> {formatTimeAgo(task.timestamp)}</span>
                    </Tooltip>
                    
                    {task.finishedOn && (
                        <Tooltip title={`Finished: ${new Date(task.finishedOn).toLocaleString()}`}>
                            <span style={{ 
                                color: task.status === 'completed' ? '#52c41a' : '#f5222d',
                                fontWeight: 500
                            }}>
                                <CheckCircleOutlined/> {formatTimeAgo(task.finishedOn)}
                            </span>
                        </Tooltip>
                    )}
                    
                    {task.processedOn && !task.finishedOn && (
                        <Tooltip title={`Started: ${new Date(task.processedOn).toLocaleString()}`}>
                            <span style={{ color: '#1890ff' }}>
                                ▶ {formatTimeAgo(task.processedOn)}
                            </span>
                        </Tooltip>
                    )}
                </div>
            </div>

            {/* Progress bar for active tasks */}
            {task.status === 'active' && progress > 0 && (
                <div style={{ marginBottom: 8 }}>
                    <Progress
                        percent={progress}
                        size="small"
                        status="active"
                        format={(percent) => `${percent}%`}
                        strokeWidth={4}
                        strokeColor={{
                            '0%': '#108ee9',
                            '100%': '#87d068',
                        }}
                        style={{ marginBottom: 0 }}
                    />
                </div>
            )}

            {/* Error details for failed tasks */}
            {task.status === 'failed' && task.failedReason && (
                <div style={{
                    marginTop: 8,
                    padding: 10,
                    backgroundColor: '#fff2f0',
                    border: '1px solid #ffccc7',
                    borderRadius: 6,
                    borderLeft: '4px solid #ff4d4f'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 14 }} />
                        <Text strong style={{ fontSize: 12, color: '#ff4d4f' }}>
                            Task Failed
                        </Text>
                    </div>
                    <Text style={{ fontSize: 11, color: '#666', lineHeight: 1.4 }}>
                        {task.failedReason}
                    </Text>
                </div>
            )}

            {/* Additional task data */}
            {displayInfo.extraInfo && (
                <div style={{
                    marginTop: 8,
                    padding: 8,
                    backgroundColor: '#f6ffed',
                    border: '1px solid #b7eb8f',
                    borderRadius: 4,
                    borderLeft: '3px solid #52c41a'
                }}>
                    <Text style={{ fontSize: 11, color: '#389e0d', fontWeight: 500 }}>
                        {displayInfo.extraInfo}
                    </Text>
                </div>
            )}

            {/* Task Details */}
            {showDetails && (
                <div style={{
                    marginTop: 8,
                    padding: 10,
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRadius: 6,
                    borderLeft: '3px solid #6c757d'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                        <InfoCircleOutlined style={{ color: '#6c757d', fontSize: 12 }} />
                        <Text strong style={{ fontSize: 11, color: '#495057' }}>
                            Task Data
                        </Text>
                    </div>
                    <pre style={{
                        fontSize: 10,
                        fontFamily: 'Monaco, Consolas, monospace',
                        backgroundColor: '#fff',
                        padding: 8,
                        borderRadius: 4,
                        border: '1px solid #dee2e6',
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: 200,
                        overflow: 'auto',
                        color: '#495057',
                        lineHeight: 1.4
                    }}>
                        {JSON.stringify(task.data, null, 2)}
                    </pre>
                </div>
            )}
        </div>
    );
};

export default TaskItem;
